import PageHero from "@/components/PageHero";
import { PROJECTS } from "@/lib/data";
import { motion } from "framer-motion";
import { MapPin, Calendar, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Projects() {
  const items = PROJECTS.filter(p => p.upcoming);

  return (
    <div data-testid="projects-page">
      <PageHero
        eyebrow="Upcoming Projects"
        title={<>Building the <em className="not-italic font-serif italic text-shramik-green">next phase</em> of Shramik Group.</>}
        subtitle="A ₹120 Crore capex program to establish Shramik Milk & Food Processing as a premium ingredient supplier — with modern SCADA-controlled processing, dryer, evaporator and HEPA-filtered powder packing."
      />

      <section className="py-16">
        <div className="container-page">
          {/* Highlights strip */}
          <div className="mb-14 grid md:grid-cols-4 gap-4" data-testid="upcoming-highlights">
            {[
              ["₹120 Cr", "Total Capex"],
              ["10,000 sq.m", "New Facility"],
              ["5 LLPD", "Additional Capacity"],
              ["400 KL/day", "Milk Procurement Potential"],
            ].map(([k,l]) => (
              <div key={l} className="p-6 rounded-2xl bg-shramik-greenLight/50 border border-shramik-greenLight">
                <div className="font-display text-2xl md:text-3xl font-light text-shramik-green leading-none">{k}</div>
                <div className="mt-2 text-xs uppercase tracking-widest text-slate-600 font-semibold">{l}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {items.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.5, delay:i*0.05}}
                className="group relative rounded-3xl overflow-hidden bg-white border border-slate-100 hover:border-shramik-green hover:shadow-xl transition-all p-8 flex flex-col"
                data-testid={`project-${p.id}`}
              >
                <div className="inline-flex self-start items-center gap-1.5 px-3 py-1 rounded-full bg-shramik-gold/15 text-shramik-ink border border-shramik-gold/40 text-[10px] uppercase tracking-widest font-bold mb-6">
                  <Sparkles className="w-3 h-3 text-shramik-gold"/> Upcoming
                </div>
                <h3 className="font-display font-medium text-2xl leading-tight text-shramik-ink">{p.title}</h3>
                <p className="mt-4 text-slate-600 leading-relaxed flex-1">{p.d}</p>
                <div className="mt-8 pt-5 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                  <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-shramik-green"/> {p.year}</span>
                  <span className="flex items-center gap-1.5 text-right"><MapPin className="w-3.5 h-3.5 text-shramik-green"/> {p.location}</span>
                </div>
              </motion.article>
            ))}
          </div>

          {items.length === 0 && (
            <div className="text-center py-16 text-slate-500">No upcoming projects to display yet.</div>
          )}

          {/* CTA */}
          <div className="mt-16 p-8 md:p-12 rounded-3xl bg-shramik-ink text-white relative overflow-hidden" data-testid="projects-cta">
            <div className="orb bg-shramik-green/40 w-[420px] h-[420px] -top-40 -right-20"/>
            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="text-xs uppercase tracking-[0.22em] text-shramik-gold mb-3">Partnerships</div>
                <h3 className="font-display font-light text-3xl md:text-4xl leading-tight text-balance">
                  Interested in <em className="not-italic font-serif italic text-shramik-gold">partnering</em> on our expansion?
                </h3>
                <p className="mt-4 text-white/80">We're inviting institutional buyers, ingredient customers, technology partners and financial collaborators as we build Shramik's next chapter.</p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Link to="/contact" data-testid="projects-cta-btn" className="btn-primary bg-shramik-gold text-shramik-ink hover:bg-white">Get in touch <ArrowRight className="w-4 h-4"/></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
